/**
 * PostHog Analytics Service for SortingCat AI Frontend
 * Comprehensive tracking for user interactions and file processing events
 * 
 * @version 1.0.0
 * @date 2025-09-18
 */

import posthog from 'posthog-js';

// Initialize PostHog (Create React App uses process.env, not import.meta.env)
const isProduction = process.env.NODE_ENV === 'production';
const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost = process.env.REACT_APP_POSTHOG_HOST;

if (posthogKey && typeof window !== 'undefined') {
  posthog.init(posthogKey, {
    api_host: posthogHost || 'https://app.posthog.com',
    disable_session_recording: !isProduction, // Only record in production
    capture_pageview: false, // We'll manually capture pageviews
    capture_pageleave: true,
    autocapture: false, // We'll manually track events for better control
    persistence: 'localStorage',
    bootstrap: {
      distinctID: generateAnonymousId()
    },
    // Disable automatic network capture for file uploads and large requests
    capture_performance: false,
    mask_all_element_attributes: true,
    mask_all_text: false,
    // Prevent large payload captures that cause 413 errors
    sanitize_properties: (properties: any, _event: string) => {
      // Remove file objects and large payloads from all events
      if (properties && typeof properties === 'object') {
        const sanitized = { ...properties };
        
        // Remove any FormData, File objects, or ArrayBuffers
        Object.keys(sanitized).forEach(key => {
          const value = sanitized[key];
          if (value instanceof FormData || 
              value instanceof File || 
              value instanceof ArrayBuffer ||
              value instanceof Blob ||
              (Array.isArray(value) && value.some(item => 
                item instanceof File || item instanceof Blob
              ))) {
            delete sanitized[key];
          }
          
          // Remove large string payloads (>10KB)
          if (typeof value === 'string' && value.length > 10240) {
            sanitized[key] = '[LARGE_PAYLOAD_FILTERED]';
          }
          
          // Remove large arrays or objects (>1000 items)
          if (Array.isArray(value) && value.length > 1000) {
            sanitized[key] = `[LARGE_ARRAY_FILTERED_${value.length}_ITEMS]`;
          }
        });
        
        return sanitized;
      }
      return properties;
    }
  });
  
  console.log('📊 PostHog analytics initialized with payload filtering');
} else {
  console.log('📊 PostHog analytics disabled (no API key or server-side)');
}

// Generate anonymous ID for users
function generateAnonymousId(): string {
  return `anon_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
}

// Export analytics service
export const analytics = {
  // Page tracking
  page(pageName: string, properties?: Record<string, any>) {
    if (!posthogKey) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties
    });
  },

  // File selection events
  filesSelected(data: {
    fileCount: number;
    totalSizeMB: number;
    fileTypes: string[];
    hasHeic: boolean;
    hasMixedTypes: boolean;
    selectionMethod: 'upload' | 'drag_drop';
  }) {
    if (!posthogKey) return;
    // Limit file types array to prevent large payloads
    const limitedFileTypes = data.fileTypes.length > 20 
      ? [...data.fileTypes.slice(0, 20), `..._and_${data.fileTypes.length - 20}_more`]
      : data.fileTypes;
      
    posthog.capture('files_selected', {
      file_count: data.fileCount,
      total_size_mb: Math.round(data.totalSizeMB * 100) / 100,
      file_types: limitedFileTypes,
      unique_file_types: [...new Set(data.fileTypes)].slice(0, 10), // Modern ES2022 spread syntax
      has_heic: data.hasHeic,
      has_mixed_types: data.hasMixedTypes,
      selection_method: data.selectionMethod,
      timestamp: new Date().toISOString()
    });
  },

  // File validation events
  validationFailed(data: {
    fileName: string;
    fileType: string;
    fileSizeMB: number;
    reason: 'invalid_type' | 'size_limit' | 'mime_mismatch';
  }) {
    if (!posthogKey) return;
    posthog.capture('validation_failed', {
      file_name: data.fileName,
      file_type: data.fileType,
      file_size_mb: Math.round(data.fileSizeMB * 100) / 100,
      failure_reason: data.reason,
      validation_layer: 'frontend'
    });
  },

  // Upload events
  uploadStarted(data: {
    jobId: string;
    fileCount: number;
    totalSizeMB: number;
    hasCustomCategories: boolean;
    email: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('upload_started', {
      job_id: data.jobId,
      file_count: data.fileCount,
      total_size_mb: Math.round(data.totalSizeMB * 100) / 100,
      has_custom_categories: data.hasCustomCategories,
      email: data.email,
      email_domain: data.email.split('@')[1] || 'unknown'
    });
  },

  uploadCompleted(data: {
    jobId: string;
    success: boolean;
    error?: string;
    processingTimeMs: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('upload_completed', {
      job_id: data.jobId,
      success: data.success,
      error: data.error,
      processing_time_ms: data.processingTimeMs
    });
  },

  // Network request tracking
  networkRequest(data: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    url: string;
    status: number;
    responseTimeMs: number;
    fileExtensions?: string[];
    error?: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('network_request', {
      method: data.method,
      url: data.url.replace(/\/[a-f0-9-]{36}/g, '/:id'), // Anonymize IDs in URLs
      status: data.status,
      response_time_ms: data.responseTimeMs,
      file_extensions: data.fileExtensions,
      success: data.status >= 200 && data.status < 300,
      error: data.error
    });
  },

  // Error events
  error(error: string, context?: Record<string, any>) {
    if (!posthogKey) return;
    posthog.capture('frontend_error', {
      error_message: error,
      error_context: context,
      url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  },

  // Job monitoring
  jobStatusChecked(data: {
    jobId: string;
    status: string;
    processedFiles: number;
    totalFiles: number;
    checkCount: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('job_status_checked', {
      job_id: data.jobId,
      status: data.status,
      processed_files: data.processedFiles,
      total_files: data.totalFiles,
      check_count: data.checkCount,
      completion_percentage: data.totalFiles > 0 
        ? Math.round((data.processedFiles / data.totalFiles) * 100) 
        : 0
    });
  },

  // Job completion event
  jobCompleted(data: {
    jobId: string;
    success: boolean;
    fileCount: number;
    processedFiles: number;
    failedFiles: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('job_completed', {
      job_id: data.jobId,
      success: data.success,
      file_count: data.fileCount,
      processed_files: data.processedFiles,
      failed_files: data.failedFiles,
      completion_percentage: data.fileCount > 0
        ? Math.round((data.processedFiles / data.fileCount) * 100)
        : 0
    });
  },

  // Download events
  downloadStarted(data: {
    jobId: string;
    downloadType: 'zip' | 'individual';
  }) {
    if (!posthogKey) return;
    posthog.capture('download_started', {
      job_id: data.jobId,
      download_type: data.downloadType
    });
  },

  downloadCompleted(data: {
    jobId: string;
    success: boolean;
    downloadSizeMB?: number;
    error?: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('download_completed', {
      job_id: data.jobId,
      success: data.success,
      download_size_mb: data.downloadSizeMB ? Math.round(data.downloadSizeMB * 100) / 100 : undefined,
      error: data.error
    });
  },

  // User identification
  identifyUser(email: string, properties?: Record<string, any>) {
    if (!posthogKey) return;
    posthog.identify(email, {
      email,
      email_domain: email.split('@')[1] || 'unknown',
      ...properties
    });
  },

  // Performance tracking
  performanceMetric(metric: string, value: number, unit: string, context?: Record<string, any>) {
    if (!posthogKey) return;
    posthog.capture('performance_metric', {
      metric_name: metric,
      metric_value: value,
      metric_unit: unit,
      ...context
    });
  },

  // Credit system tracking
  creditBalanceChecked(data: {
    userId: string;
    balance: number;
    totalGranted: number;
    totalConsumed: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_balance_checked', {
      user_id: data.userId,
      balance: data.balance,
      total_granted: data.totalGranted,
      total_consumed: data.totalConsumed,
      utilization_percentage: data.totalGranted > 0
        ? Math.round((data.totalConsumed / data.totalGranted) * 100)
        : 0
    });
  },

  creditGranted(data: {
    userId: string;
    amount: number;
    reason: string;
    plan?: string;
    balanceBefore: number;
    balanceAfter: number;
    transactionId: string;
    idempotencyKey?: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_granted', {
      user_id: data.userId,
      amount: data.amount,
      reason: data.reason,
      plan: data.plan,
      balance_before: data.balanceBefore,
      balance_after: data.balanceAfter,
      transaction_id: data.transactionId,
      idempotency_key: data.idempotencyKey,
      grant_type: data.reason.includes('signup') ? 'signup_bonus' :
                  data.reason.includes('renewal') ? 'subscription_renewal' :
                  data.reason.includes('upgrade') ? 'upgrade_proration' :
                  'manual_grant',
      timestamp: new Date().toISOString()
    });
  },

  creditDeducted(data: {
    userId: string;
    amount: number;
    reason: string;
    jobId?: string;
    fileCount?: number;
    balanceBefore: number;
    balanceAfter: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_deducted', {
      user_id: data.userId,
      amount: data.amount,
      reason: data.reason,
      job_id: data.jobId,
      file_count: data.fileCount,
      balance_before: data.balanceBefore,
      balance_after: data.balanceAfter,
      cost_per_file: data.fileCount ? Math.round((data.amount / data.fileCount) * 100) / 100 : undefined,
      timestamp: new Date().toISOString()
    });
  },

  creditRefunded(data: {
    userId: string;
    amount: number;
    reason: string;
    jobId?: string;
    balanceBefore: number;
    balanceAfter: number;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_refunded', {
      user_id: data.userId,
      amount: data.amount,
      reason: data.reason,
      job_id: data.jobId,
      balance_before: data.balanceBefore,
      balance_after: data.balanceAfter,
      timestamp: new Date().toISOString()
    });
  },

  creditInsufficient(data: {
    userId: string;
    requiredCredits: number;
    availableBalance: number;
    shortfall: number;
    attemptedAction: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_insufficient', {
      user_id: data.userId,
      required_credits: data.requiredCredits,
      available_balance: data.availableBalance,
      shortfall: data.shortfall,
      attempted_action: data.attemptedAction,
      shortfall_percentage: Math.round((data.shortfall / data.requiredCredits) * 100),
      timestamp: new Date().toISOString()
    });
  },

  subscriptionChanged(data: {
    userId: string;
    previousPlan?: string;
    newPlan: string;
    changeType: 'new_subscription' | 'upgrade' | 'downgrade' | 'renewal' | 'cancellation';
    creditsGranted?: number;
    scheduledDowngrade?: boolean;
    effectiveDate?: string;
  }) {
    if (!posthogKey) return;
    posthog.capture('subscription_changed', {
      user_id: data.userId,
      previous_plan: data.previousPlan || 'none',
      new_plan: data.newPlan,
      change_type: data.changeType,
      credits_granted: data.creditsGranted || 0,
      scheduled_downgrade: data.scheduledDowngrade || false,
      effective_date: data.effectiveDate,
      is_immediate: !data.scheduledDowngrade,
      timestamp: new Date().toISOString()
    });
  },

  creditSystemError(data: {
    userId: string;
    errorType: 'idempotency_violation' | 'race_condition' | 'timeout' | 'insufficient_credits' | 'unknown';
    errorMessage: string;
    operation: string;
    retryable: boolean;
    context?: Record<string, any>;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_system_error', {
      user_id: data.userId,
      error_type: data.errorType,
      error_message: data.errorMessage,
      operation: data.operation,
      retryable: data.retryable,
      context: data.context,
      timestamp: new Date().toISOString()
    });
  },

  creditSystemSecurity(data: {
    userId: string;
    eventType: 'idempotency_blocked' | 'race_condition_prevented' | 'stale_lock_recovered' | 'exploit_attempt_blocked';
    details: string;
    metadata?: Record<string, any>;
  }) {
    if (!posthogKey) return;
    posthog.capture('credit_system_security', {
      user_id: data.userId,
      event_type: data.eventType,
      details: data.details,
      metadata: data.metadata,
      severity: data.eventType.includes('exploit') ? 'critical' :
                data.eventType.includes('blocked') ? 'high' : 'medium',
      timestamp: new Date().toISOString()
    });
  }
};

// Export for manual PostHog access if needed
export { posthog };
export default analytics;